(() => {
  const cleanups = new WeakMap();
  const hover = matchMedia('(hover: hover) and (pointer: fine)');
  function setup(scope) {
    scope.querySelectorAll('.oh-nav').forEach(nav => {
      if (cleanups.has(nav)) return;
      const controller = new AbortController();
      const listen = (node,event,handler) => node.addEventListener(event,handler,{signal:controller.signal});
      const entries = [...nav.querySelectorAll('[data-nav-dropdown]')];
      const mobile = matchMedia('(max-width:749px)');
      const slot = nav.closest('.oh-nav-slot');
      const toggle = nav.querySelector('.oh-mobile-menu-toggle');
      const backdrop = slot?.querySelector('.oh-nav-backdrop');
      const drawer = nav.querySelector('.oh-nav-links');
      const controls = nav.querySelector('.oh-tools');
      const brand = nav.querySelector('.oh-compact-brand');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      let locked = null, normalHeight = nav.getBoundingClientRect().height, rowOffset = 0, frame = 0;
      let morphs = [];
      const stopMorph = () => { morphs.forEach(animation => animation.cancel()); morphs = []; };
      const revealMenuControls = entering => {
        if (reducedMotion.matches || !entering) return;
        // The menu button remains visible; only the arriving shark fades in.
        [brand].forEach(node => morphs.push(node.animate(
          [{opacity:0},{opacity:1}], {duration:480,easing:'ease-out'}
        )));
      };
      let menuOpener = toggle;
      const viewportBottom = () => window.visualViewport ? visualViewport.offsetTop + visualViewport.height : innerHeight;
      const fitDrawer = () => {
        if (!nav.classList.contains('is-compact') && !locked) return;
        nav.style.setProperty('--oh-drawer-height', `${Math.max(44, viewportBottom() - nav.getBoundingClientRect().bottom)}px`);
        if (nav.classList.contains('is-compact')) document.documentElement.style.setProperty('--oh-compact-height', `${nav.offsetHeight}px`);
      };
      const closeDrawer = (returnFocus = false) => {
        if (!locked) return;
        const saved = locked;
        locked = null;
        nav.classList.remove('is-menu-open');
        if (!nav.classList.contains('is-compact')) {
          const parent = drawer.parentElement;
          (parent.moveBefore || parent.insertBefore).call(parent, drawer, controls);
        }
        [toggle,brand].forEach(node=>{node.setAttribute('aria-expanded','false');node.setAttribute('aria-label','Open navigation menu');});
        backdrop.hidden = true;
        Object.assign(document.body.style, saved.styles);
        saved.inert.forEach(([node, value]) => node.inert = value);
        window.scrollTo({top:saved.y, behavior:'instant'});
        if (returnFocus) menuOpener.focus({preventScroll:true});
      };
      const updateCompact = () => {
        if (!slot || !toggle) return;
        document.documentElement.classList.add('oh-mobile-header-ready');
        if (locked) { fitDrawer(); return; }
        const compact = nav.classList.contains('is-compact');
        if (!compact) {
          if (mobile.matches) {
            // Fit the complete row using its actual labels and fonts, rather
            // than keeping Dive Trips below for every phone-sized viewport.
            nav.classList.remove('oh-nav-trips-below');
            nav.classList.add('oh-nav-trips-inline');
            const links = [...drawer.children].filter(node => node.matches('[data-nav-dropdown],.oh-nav-mobile-utility'));
            const first = links[0]?.getBoundingClientRect();
            const fits = links.every(node => {
              const box = node.getBoundingClientRect();
              return Math.abs(box.top + box.height / 2 - first.top - first.height / 2) < 1;
            });
            nav.classList.toggle('oh-nav-trips-inline', fits);
            nav.classList.toggle('oh-nav-trips-below', !fits);
            const training = entries[1]?.getBoundingClientRect();
            const toolBox = controls.getBoundingClientRect();
            const gap = parseFloat(getComputedStyle(controls).columnGap) || 0;
            if (training) nav.style.setProperty('--oh-full-phone-search-width', `${Math.max(80, toolBox.right - training.left - 48 - gap)}px`);
          } else {
            nav.classList.remove('oh-nav-trips-below', 'oh-nav-trips-inline');
            nav.style.removeProperty('--oh-full-phone-search-width');
          }
          normalHeight = nav.getBoundingClientRect().height;
          rowOffset = controls.getBoundingClientRect().top - nav.getBoundingClientRect().top;
        }
        const top = slot.getBoundingClientRect().top + scrollY;
        // Pin precisely when the search row reaches the viewport top. The old
        // bottom-of-navigation threshold let the field disappear before returning.
        const threshold = top + rowOffset;
        const desired = compact ? scrollY >= threshold - 1 : scrollY >= threshold;
        if (desired !== compact) {
          const active = document.activeElement;
          const hadFocus = nav.contains(active);
          stopMorph();
          closeAll();
          slot.style.minHeight = desired ? `${normalHeight}px` : '';
          nav.classList.toggle('is-compact', desired);
          const parent = drawer.parentElement;
          const move = parent.moveBefore || parent.insertBefore;
          move.call(parent, desired ? controls : drawer, desired ? drawer : controls);
          if (hadFocus) {
            const target = active.getClientRects().length ? active : desired ? toggle : nav.closest('header').querySelector('.oh-lockup');
            target?.focus({preventScroll:true});
          }
          revealMenuControls(desired);
        }
        fitDrawer();
      };
      const queueCompact = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => { frame = 0; updateCompact(); });
      };
      const alignFish = () => {
        if (!mobile.matches) return;
        const masthead = nav.closest('header');
        const identity = masthead?.querySelector('.oh-identity');
        const signature = masthead?.querySelector('.oh-lockup img');
        const fish = masthead?.querySelector('.oh-fish');
        if (!identity || !signature || !fish) return;
        const host = identity.getBoundingClientRect(), shark = signature.getBoundingClientRect(), flag = fish.getBoundingClientRect();
        // The fish stays at the right edge throughout phone widths. Only its
        // vertical alignment follows the signature; there is no overlap switch.
        identity.style.setProperty('--oh-fish-top', `${shark.top - host.top + shark.height * .5 - flag.height * .5}px`);
      };
      const layoutObserver = new ResizeObserver(() => { alignFish(); queueCompact(); });
      layoutObserver.observe(drawer);
      entries.forEach(entry => layoutObserver.observe(entry.querySelector('summary')));
      const identity = nav.closest('header')?.querySelector('.oh-identity');
      if (identity) layoutObserver.observe(identity);
      const lockup = nav.closest('header')?.querySelector('.oh-lockup');
      if (lockup) layoutObserver.observe(lockup);
      document.fonts.ready.then(() => { if (!controller.signal.aborted) { alignFish(); queueCompact(); } });
      if (toggle && backdrop) {
        const toggleDrawer = event => {
          menuOpener = event.currentTarget;
          if (locked) { closeAll(); closeDrawer(true); return; }
          closeAll();
          const styles = Object.fromEntries(['position','top','left','right','width'].map(key => [key,document.body.style[key]]));
          const inert = [];
          // Isolate the overlay while preserving its containing header ancestors.
          let branch = nav;
          while (branch.parentElement && branch.parentElement !== document.documentElement) {
            [...branch.parentElement.children].filter(node => node !== branch && node !== backdrop && !node.contains(backdrop)).forEach(node => {
              inert.push([node,node.inert]); node.inert = true;
            });
            branch = branch.parentElement;
          }
          locked = {y:scrollY, styles, inert};
          Object.assign(document.body.style,{position:'fixed',top:`-${locked.y}px`,left:'0',right:'0',width:'100%'});
          nav.classList.add('is-menu-open');
          const parent = drawer.parentElement;
          (parent.moveBefore || parent.insertBefore).call(parent, controls, drawer);
          [toggle,brand].forEach(node=>{node.setAttribute('aria-expanded','true');node.setAttribute('aria-label','Close navigation menu');});
          backdrop.hidden = false;
          fitDrawer();
          drawer.querySelector('summary,a')?.focus({preventScroll:true});
        };
        [toggle,brand].forEach(node=>listen(node,'click',toggleDrawer));
        listen(backdrop,'click',()=>{closeAll();closeDrawer(true);});
        listen(nav,'click',event=>{if(event.target.closest('a[href]')){closeAll();closeDrawer();}});
        listen(nav,'keydown',event=>{
          if(event.key !== 'Tab' || !locked)return;
          const controls=[...nav.querySelectorAll('a[href],button,input:not([type="hidden"]),summary')].filter(node=>node.getClientRects().length && !node.disabled);
          const first=controls[0],last=controls.at(-1);
          if(event.shiftKey && document.activeElement===first){event.preventDefault();last.focus();}
          else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first.focus();}
        });
        listen(window,'scroll',queueCompact);
        listen(window,'resize',()=>{stopMorph();queueCompact();});
        listen(nav,'focusout',queueCompact);
        listen(mobile,'change',queueCompact);
        if(window.visualViewport)listen(visualViewport,'resize',fitDrawer);
      }
      const timers = new Map();
      const clear = entry => { clearTimeout(timers.get(entry)); timers.delete(entry); };
      const close = entry => { clear(entry); entry.open=false; };
      const fit = (entry,opening=false) => {
        if(nav.classList.contains('is-compact') || locked){fitDrawer();return;}
        const panel=entry.querySelector('.oh-nav-panel');
        const bottom=()=>window.visualViewport ? visualViewport.offsetTop+visualViewport.height : innerHeight;
        // A menu near the bottom needs room to open. Move its navigation bar
        // into view once; subsequent scrolling stays inside the fitted panel.
        if(opening && bottom()-panel.getBoundingClientRect().top<180)window.scrollTo({top:Math.max(0,scrollY+nav.getBoundingClientRect().top-4),behavior:'instant'});
        panel.style.setProperty('--oh-nav-available',`${Math.max(48,Math.min(650,bottom()-panel.getBoundingClientRect().top-12))}px`);
      };
      const open = entry => { clear(entry); entries.forEach(other => {if(other!==entry)close(other);});entry.open=true;fit(entry,true); };
      const closeAll = () => entries.forEach(close);
      entries.forEach(entry => {
        const summary=entry.querySelector('summary');
        const closeButton=entry.querySelector('[data-nav-close]');
        if(closeButton) { closeButton.hidden=false;listen(closeButton,'click',()=>{close(entry);summary.focus();}); }
        listen(entry,'toggle',()=>{if(entry.open){entries.forEach(other=>{if(other!==entry)close(other);});fit(entry,true);}});
        listen(entry,'pointerenter',event=>{
          if(nav.classList.contains('is-compact') || locked || !hover.matches || event.pointerType!=='mouse')return;
          clear(entry);timers.set(entry,setTimeout(()=>open(entry),120));
        });
        listen(entry,'pointerleave',event=>{
          clear(entry);
          if(nav.classList.contains('is-compact') || locked || !hover.matches || event.pointerType!=='mouse' || entry.contains(document.activeElement))return;
          timers.set(entry,setTimeout(()=>close(entry),200));
        });
        listen(entry,'focusout',event=>{
          // In the stacked drawer, pointer-down moves focus before click. Closing
          // a section here moves lower headings out from under the pointer, so
          // leave sibling switching to their completed native toggle instead.
          if((nav.classList.contains('is-compact') || locked) && nav.contains(event.relatedTarget))return;
          if(event.relatedTarget && !entry.contains(event.relatedTarget))close(entry);
        });
        listen(summary,'keydown',event=>{
          if(event.key!=='ArrowDown' && event.key!=='ArrowUp')return;
          event.preventDefault();open(entry);
          const links=[...entry.querySelectorAll('.oh-nav-panel a,.oh-nav-course-group>summary')].filter(node=>node.getClientRects().length);
          (event.key==='ArrowDown'?links[0]:links[links.length-1])?.focus();
        });
        listen(entry,'click',event=>{if(event.target.closest('a[href]'))close(entry);});
        entry.querySelectorAll('[data-nav-course-group]').forEach(group=>{
          listen(group,'toggle',()=>{
            if(group.open)entry.querySelectorAll('[data-nav-course-group]').forEach(other=>{if(other!==group)other.open=false;});
            if(entry.open)fit(entry);
          });
        });
      });
      listen(document,'keydown',event=>{
        if(event.key!=='Escape')return;
        const active=entries.find(entry=>entry.open);
        if(!active){if(locked){event.preventDefault();closeDrawer(true);}return;}
        event.preventDefault();
        const group=event.target.closest?.('[data-nav-course-group][open]');
        if(group){group.open=false;group.querySelector('summary').focus();return;}
        closeAll();active.querySelector('summary').focus();
      });
      listen(document,'click',event=>{if(!nav.contains(event.target))closeAll();});
      const refit=()=>entries.filter(entry=>entry.open).forEach(entry=>fit(entry));
      const resize=()=>entries.filter(entry=>entry.open).forEach(entry=>fit(entry,true));
      listen(window,'resize',resize);listen(window,'scroll',refit);
      if(window.visualViewport){listen(visualViewport,'resize',resize);listen(visualViewport,'scroll',refit);}
      listen(document,'shopify:block:select',event=>{
        const entry=event.target.closest?.('[data-nav-dropdown]');
        if(entry && nav.contains(entry)){open(entry);const group=event.target.closest('[data-nav-course-group]');if(group)group.open=true;}
      });
      updateCompact();
      alignFish();
      cleanups.set(nav,()=>{closeAll();closeDrawer();stopMorph();layoutObserver.disconnect();cancelAnimationFrame(frame);controller.abort();document.documentElement.classList.remove('oh-mobile-header-ready');cleanups.delete(nav);});
    });
  }
  setup(document);
  document.addEventListener('shopify:section:load',event=>setup(event.target));
  document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('.oh-nav').forEach(nav=>cleanups.get(nav)?.()));
})();
